import { z } from "zod";
import { zKeyedRecord } from "./zod/utils.js";

import { AdminLearnSteps } from "./learnTypes.js";
import { getNormalEnum } from "./pseudoEnums.js";

export const ContextSetupStepLiteralValues = [
  /**
   * This step is used to track whether we've checked an existing org for completion steps
   */
  z.literal("HAS_AUTOCOMPLETED_FOR_EXISTING_ORG"),
  z.literal("CONNECT_DATA"),

  z.literal("CHOOSE_DEFAULT_DATA_CONNECTION"),
  z.literal("ADD_DATA_DESCRIPTIONS"),

  z.literal("ENDORSE_DATA"),
  z.literal("INSTALL_CONTEXT_MANAGEMENT_SKILL"),
  z.literal("ADD_RULES"),
  z.literal("ADD_SEMANTIC_MODEL"),
  z.literal("ADD_REFERENCE_REPOS"),
  z.literal("ADD_MCP_SERVERS"),
] as const;
export const ContextSetupStepLiteral = z.union(ContextSetupStepLiteralValues);
export type ContextSetupStepLiteral = z.infer<typeof ContextSetupStepLiteral>;

export const ContextSetupStep = getNormalEnum(ContextSetupStepLiteral);

export const GroupedContextSteps = {
  [ContextSetupStep.CONNECT_DATA]: [
    ContextSetupStep.CHOOSE_DEFAULT_DATA_CONNECTION,
    ContextSetupStep.ADD_DATA_DESCRIPTIONS,
  ],
  [ContextSetupStep.ENDORSE_DATA]: [],
  [ContextSetupStep.ADD_RULES]: [
    ContextSetupStep.INSTALL_CONTEXT_MANAGEMENT_SKILL,
    ContextSetupStep.ADD_RULES,
  ],
  [ContextSetupStep.ADD_REFERENCE_REPOS]: [],
  [ContextSetupStep.ADD_MCP_SERVERS]: [],
  [ContextSetupStep.ADD_SEMANTIC_MODEL]: [],
} as const;

/**
 * Substeps that are shown in context setup but do not need to be completed
 * for their parent top-level step to count as done.
 */
export const OptionalContextSetupSteps: ReadonlySet<ContextSetupStepLiteral> =
  new Set([
    ContextSetupStep.ADD_DATA_DESCRIPTIONS,
    ContextSetupStep.INSTALL_CONTEXT_MANAGEMENT_SKILL,
  ]);

export const ContextSetupStepToTrackingStep: Record<
  ContextSetupStepLiteral,
  OnboardingFlowStep | null
> = {
  [ContextSetupStep.CONNECT_DATA]: "CONTEXT_SETUP_CONNECT_DATA",
  [ContextSetupStep.CHOOSE_DEFAULT_DATA_CONNECTION]:
    "CONTEXT_SETUP_CHOOSE_DEFAULT_DATA_CONNECTION",
  [ContextSetupStep.ADD_DATA_DESCRIPTIONS]:
    "CONTEXT_SETUP_ADD_DATA_DESCRIPTIONS",
  [ContextSetupStep.ENDORSE_DATA]: "CONTEXT_SETUP_ENDORSE_DATA",
  [ContextSetupStep.INSTALL_CONTEXT_MANAGEMENT_SKILL]:
    "CONTEXT_SETUP_INSTALL_CONTEXT_MANAGEMENT_SKILL",
  [ContextSetupStep.ADD_RULES]: "CONTEXT_SETUP_ADD_RULES",
  [ContextSetupStep.ADD_SEMANTIC_MODEL]: "CONTEXT_SETUP_ADD_SEMANTIC_MODEL",
  [ContextSetupStep.ADD_REFERENCE_REPOS]: "CONTEXT_SETUP_ADD_REFERENCE_REPOS",
  [ContextSetupStep.ADD_MCP_SERVERS]: "CONTEXT_SETUP_ADD_MCP_SERVERS",
  [ContextSetupStep.HAS_AUTOCOMPLETED_FOR_EXISTING_ORG]: null,
};

export type TopLevelContextStep = keyof typeof GroupedContextSteps;

export const REQUIRED_CONTEXT_STEPS: readonly ContextSetupStepLiteral[] = [
  ContextSetupStep.CHOOSE_DEFAULT_DATA_CONNECTION,
  ContextSetupStep.ENDORSE_DATA,
  ContextSetupStep.ADD_RULES,
];

const WorkspaceSetupStepLiteralValues = [
  ...ContextSetupStepLiteralValues,
  z.literal("DISPLAY_NAME"),
  z.literal("CREATE_EVALS"),
  /** @deprecated , but keeping around for historical data */
  z.literal("INVITE_TEAMMATES"),
  z.literal("WORKSPACE_DATA_CONNECTION"),
  z.literal("QUESTIONNAIRE"),
  /** @deprecated — removed from context setup UI but kept for historical data */
  z.literal("ADD_DATA_CONNECTION"),
  z.literal("ADD_DC_DESCRIPTIONS"),
  z.literal("FILTER_SCHEMA"),
  z.literal("SETUP_SCHEMA_REFRESH"),
  z.literal("TEST_AND_OPTIMIZE"),
] as const;
export const WorkspaceSetupLiteral = z.union(WorkspaceSetupStepLiteralValues);

export type WorkspaceSetupStep = z.infer<typeof WorkspaceSetupLiteral>;
export const WorkspaceSetupStep = getNormalEnum(WorkspaceSetupLiteral);
export const WorkspaceSetupValueType = z
  .union([z.boolean(), z.null()])
  .optional();
export const WorkspaceSetupSteps = zKeyedRecord(
  WorkspaceSetupLiteral,
  WorkspaceSetupValueType,
);
export type WorkspaceSetupSteps = z.infer<typeof WorkspaceSetupSteps>;

export const WorkspaceSetupStepsDefault: Record<
  WorkspaceSetupStep,
  boolean | null
> = {
  [WorkspaceSetupStep.DISPLAY_NAME]: null,
  [WorkspaceSetupStep.CREATE_EVALS]: null,
  [WorkspaceSetupStep.CONNECT_DATA]: null,
  [WorkspaceSetupStep.CHOOSE_DEFAULT_DATA_CONNECTION]: null,
  [WorkspaceSetupStep.ADD_DATA_DESCRIPTIONS]: null,
  [WorkspaceSetupStep.ENDORSE_DATA]: null,
  [WorkspaceSetupStep.INSTALL_CONTEXT_MANAGEMENT_SKILL]: null,
  [WorkspaceSetupStep.ADD_RULES]: null,
  [WorkspaceSetupStep.ADD_SEMANTIC_MODEL]: null,
  [WorkspaceSetupStep.ADD_REFERENCE_REPOS]: null,
  [WorkspaceSetupStep.ADD_MCP_SERVERS]: null,
  [WorkspaceSetupStep.HAS_AUTOCOMPLETED_FOR_EXISTING_ORG]: null,
  /** @deprecated — kept for historical data */
  [WorkspaceSetupStep.INVITE_TEAMMATES]: null,
  [WorkspaceSetupStep.WORKSPACE_DATA_CONNECTION]: null,
  [WorkspaceSetupStep.QUESTIONNAIRE]: null,
  [WorkspaceSetupStep.ADD_DATA_CONNECTION]: null,
  [WorkspaceSetupStep.ADD_DC_DESCRIPTIONS]: null,
  [WorkspaceSetupStep.FILTER_SCHEMA]: null,
  [WorkspaceSetupStep.SETUP_SCHEMA_REFRESH]: null,
  [WorkspaceSetupStep.TEST_AND_OPTIMIZE]: null,
};

/**
 * True when a parent's required (non-optional) substeps are all complete but
 * the parent itself is not yet marked — e.g. after a required substep becomes
 * optional and CONNECT_DATA was never marked.
 */
export const shouldMarkContextParentComplete = (
  parentStep: TopLevelContextStep,
  steps: WorkspaceSetupSteps,
): boolean => {
  const requiredSubsteps = GroupedContextSteps[parentStep].filter(
    (subStep) => !OptionalContextSetupSteps.has(subStep),
  );
  return (
    requiredSubsteps.length > 0 &&
    steps[parentStep] !== true &&
    requiredSubsteps.every((subStep) => steps[subStep] === true)
  );
};

/**
 * Parents that should be marked complete given the current step state.
 */
export const getContextParentsToMarkComplete = (
  steps: WorkspaceSetupSteps,
): TopLevelContextStep[] =>
  (Object.keys(GroupedContextSteps) as TopLevelContextStep[]).filter(
    (parentStep) => shouldMarkContextParentComplete(parentStep, steps),
  );

/**
 * True when any grouped parent needs reconcile (required substeps done, parent
 * not marked). Used by the client to re-run autocomplete after rule changes.
 */
export const needsContextParentReconcile = (
  steps?: WorkspaceSetupSteps,
): boolean =>
  steps != null && getContextParentsToMarkComplete(steps).length > 0;

export const OnboardingFlowStepLiteral = z.union([
  ...WorkspaceSetupStepLiteralValues,
  ...AdminLearnSteps.map((step) => z.literal(step)),
  z.literal("PROJECT_TOUR_STARTED"),
  z.literal("PROJECT_TOUR_LOGIC_TAB"),
  z.literal("PROJECT_TOUR_DATAFRAME"),
  z.literal("PROJECT_TOUR_SHARE"),
  z.literal("PROJECT_TOUR_SUPPORT"),
  z.literal("APP_BUILDER_TOUR_STARTED"),
  z.literal("APP_BUILDER_TOUR_OUTLINE"),
  z.literal("APP_BUILDER_TOUR_LAYOUT"),
  z.literal("APP_BUILDER_TOUR_SETTINGS"),
  z.literal("APP_BUILDER_TOUR_PUBLISH"),
  z.literal("DATA_SELECTION_FILE_TOUR_STARTED"),
  z.literal("DATA_SELECTION_FILE_TOUR_DATAFRAME"),
  z.literal("DATA_SELECTION_FILE_TOUR_ADD_CELLS"),
  z.literal("DATA_SELECTION_FILE_TOUR_APP_BUILDER"),
  z.literal("DATA_SELECTION_FILE_TOUR_SHARE"),
  z.literal("DATA_SELECTION_FILE_TOUR_SUPPORT"),
  z.literal("DATA_SELECTION_DC_TOUR_STARTED"),
  z.literal("DATA_SELECTION_DC_TOUR_DATAFRAME"),
  z.literal("DATA_SELECTION_DC_TOUR_ADD_CELLS"),
  z.literal("DATA_SELECTION_DC_TOUR_APP_BUILDER"),
  z.literal("DATA_SELECTION_DC_TOUR_SHARE"),
  z.literal("DATA_SELECTION_DC_TOUR_SUPPORT"),
  z.literal("DATA_SELECTION_DISMISSED_TOUR_STARTED"),
  z.literal("DATA_SELECTION_DISMISSED_TOUR_DATAFRAME"),
  z.literal("DATA_SELECTION_DISMISSED_TOUR_ADD_CELLS"),
  z.literal("DATA_SELECTION_DISMISSED_TOUR_APP_BUILDER"),
  z.literal("DATA_SELECTION_DISMISSED_TOUR_SHARE"),
  z.literal("DATA_SELECTION_DISMISSED_TOUR_SUPPORT"),
  z.literal("PROJECT_TOUR_MAGIC_IN_CELL_BUTTON"),
  z.literal("PROJECT_TOUR_CELL_SOURCE"),
  z.literal("PROJECT_TOUR_ADD_CELLS"),
  z.literal("PROJECT_TOUR_APP_BUILDER"),
  z.literal("DATA_SELECTION_MODAL"),
  z.literal("CREATE_DATA_CONNECTION"),
  z.literal("AI_PROMPT"),
  z.literal("AI_DATA_SOURCE"),
  z.literal("PROJECT_TOUR_AI_CREATED_DC_STARTED"),
  z.literal("PROJECT_TOUR_AI_CREATED_DC_AGENT"),
  z.literal("PROJECT_TOUR_AI_CREATED_DC_ADD_CELLS"),
  z.literal("PROJECT_TOUR_AI_CREATED_DC_APP_BUILDER"),
  z.literal("PROJECT_TOUR_AI_CREATED_DC_SHARE"),
  z.literal("PROJECT_TOUR_AI_CREATED_DC_SUPPORT"),
  z.literal("PROJECT_TOUR_AI_DISMISSED_DC_STARTED"),
  z.literal("PROJECT_TOUR_AI_DISMISSED_DC_AGENT"),
  z.literal("PROJECT_TOUR_AI_DISMISSED_DC_ADD_CELLS"),
  z.literal("PROJECT_TOUR_AI_DISMISSED_DC_APP_BUILDER"),
  z.literal("PROJECT_TOUR_AI_DISMISSED_DC_SHARE"),
  z.literal("PROJECT_TOUR_AI_DISMISSED_DC_SUPPORT"),
  z.literal("PROMPT_BAR"),
  z.literal("ONBOARDING_CHOICE"),
  z.literal("THREADS_INTRO_MODAL_PAGE_1"),
  z.literal("THREADS_INTRO_MODAL_PAGE_2"),
  z.literal("THREADS_INTRO_MODAL_PAGE_3"),

  z.literal("CONTEXT_SETUP_CONNECT_DATA"),
  z.literal("CONTEXT_SETUP_ADD_DATA_CONNECTION"),
  z.literal("CONTEXT_SETUP_CHOOSE_DEFAULT_DATA_CONNECTION"),
  z.literal("CONTEXT_SETUP_FILTER_SCHEMA"),
  z.literal("CONTEXT_SETUP_SETUP_SCHEMA_REFRESH"),
  z.literal("CONTEXT_SETUP_ADD_DATA_DESCRIPTIONS"),
  z.literal("CONTEXT_SETUP_ADD_DC_DESCRIPTIONS"),
  z.literal("CONTEXT_SETUP_ENDORSE_DATA"),
  z.literal("CONTEXT_SETUP_INSTALL_CONTEXT_MANAGEMENT_SKILL"),
  z.literal("CONTEXT_SETUP_ADD_RULES"),
  z.literal("CONTEXT_SETUP_ADD_SEMANTIC_MODEL"),
  z.literal("CONTEXT_SETUP_ADD_REFERENCE_REPOS"),
  z.literal("CONTEXT_SETUP_ADD_MCP_SERVERS"),
  z.literal("CONTEXT_SETUP_TEST_AND_OPTIMIZE"),
]);

export type OnboardingFlowStep = z.infer<typeof OnboardingFlowStepLiteral>;
export const OnboardingFlowStep = getNormalEnum(OnboardingFlowStepLiteral);

export const OnboardingFlowLiteral = z.enum([
  "WORKSPACE_SETUP",
  "USER_SETUP",
  "PROJECT_TOUR",
  "APP_BUILDER_TOUR",
  "DATA_SELECTION_DC_TOUR",
  "DATA_SELECTION_FILE_TOUR",
  "DATA_SELECTION_DEMO_TOUR",
  "DATA_SELECTION_EXISTING_DC_TOUR",
  "DATA_SELECTION_DISMISSED_TOUR",
  "PROJECT_TOUR_AI_CREATED_DC",
  "PROJECT_TOUR_AI_DISMISSED_DC",
  "DATA_SELECTION",
  "AI_ONBOARDING",
  "SETUP_CHECKLIST",
  "AI_ONBOARDING_V2",
  "MANAGER_PLUS_THREADS_MODAL",
  "CONTEXT_SETUP",
]);
export type OnboardingFlow = z.infer<typeof OnboardingFlowLiteral>;
export const OnboardingFlow = getNormalEnum(OnboardingFlowLiteral);

export const ActionTakenLiteral = z.enum([
  "SKIPPED",
  "COMPLETED",
  "SELECTED_DATA_CONNECTION",
  "SELECTED_FILE_UPLOAD",
  "SELECTED_DEMO",
  "BACK",
  "SUBMITTED_CSV_PROMPT",
  "SUBMITTED_DEMO_PROMPT",
  "SUBMIT_EXISTING_DATA_CONNECTION",
  "UPLOADED_FILE",
  "VIEWED_STEP",
  "VIEWED_POPULAR_PROJECT",
  "SUBMITTED_PROMPT",
  "SELECTED_NOTEBOOK",
  "SELECTED_THREAD",
]);
export type ActionTaken = z.infer<typeof ActionTakenLiteral>;
export const ActionTaken = getNormalEnum(ActionTakenLiteral);
